/** Hooks de fetch para las tabs del panel contextual de la Vista 360 (Fase 2b). */
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Tipos de respuesta ───────────────────────────────────────────────────────

export interface RelatedResponse {
  reference_document: {
    id: string; document_type: string; document_type_display: string
    folio: string | null; status: string; total: string
  } | null
  linked_adjustments: Array<{
    id: string; type: 'NC' | 'ND'; document_type: string; folio: string
    issue_date: string; amount: string; reason: string
    status?: string; status_display?: string
  }>
  adjustment_total: string
  adjusted_total: string
  adjustment_status: string
  generated_credit: { id: string; amount: string; status: string; status_display: string } | null
}

export interface VoucherResponse {
  accounting_status: string
  voucher: {
    id: string; number: string; status: string; status_display: string
    date: string | null; description: string
    lines: Array<{ account_code: string | null; account_name: string | null; glosa: string; debit: string; credit: string }>
  } | null
}

export interface CollectionResponse {
  collection_status: string
  total: string
  adjusted_total: string
  outstanding_amount: string
  due_date: string | null
  payments: Array<{
    id: string; payment_id: string; amount: string; payment_date: string | null
    payment_method_display: string; reference: string
    status: string; status_display: string; bank_account: string | null
  }>
  collection_logs: Array<{
    id: string; action_type: string; action_type_display: string; description: string
    contact_name: string; result: string; next_action_date: string | null
    created_by: string | null; created_at: string
  }>
  payment_links: Array<{
    id: string; gateway: string | null; status: string; amount: string
    currency: string; url: string; created_at: string; paid_at: string | null
  }>
}

export interface FactoringResponse {
  factoring_status: string
  operations: Array<{
    line_id: string; amount_ceded: string
    identificador_cesion?: string
    operation: {
      id: string; date: string | null; status: string; status_display: string; total_amount_ceded: string
      created_by_name?: string | null; created_at?: string | null
      cesion_signed?: boolean; sii_estado?: string; sii_track_id?: string; sii_received_at?: string | null
    } | null
    entity: { id: string; legal_name: string; tax_id: string } | null
  }>
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useDocument360Query<T>(key: string, docId: string, path: string, enabled: boolean) {
  return useQuery<T>({
    queryKey: ['doc360', key, docId],
    queryFn: async () => (await apiClient.get(`/sales/documents/${docId}/${path}/`)).data,
    enabled: enabled && !!docId,
  })
}

export const useDocumentRelated = (docId: string, enabled: boolean) =>
  useDocument360Query<RelatedResponse>('related', docId, 'related', enabled)

export const useDocumentVoucher = (docId: string, enabled: boolean) =>
  useDocument360Query<VoucherResponse>('voucher', docId, 'accounting-voucher', enabled)

export const useDocumentCollection = (docId: string, enabled: boolean) =>
  useDocument360Query<CollectionResponse>('collection', docId, 'collection', enabled)

export const useDocumentFactoring = (docId: string, enabled: boolean) =>
  useDocument360Query<FactoringResponse>('factoring', docId, 'factoring', enabled)
